/** Clave estable de un hijo enlazado: nodoPadre + nodo (API unidades) + descripción. */
export function buildLinkedSonSlotKey(
  nodoPadre: number | undefined | null,
  nodo: number | undefined | null,
  descripcion: string | undefined | null,
): string | null {
  if (nodoPadre === undefined || nodoPadre === null) return null
  if (nodo === undefined || nodo === null) return null
  if (!descripcion) return null
  return `${nodoPadre}-${nodo}-${descripcion}`
}

export function buildLinkedSonSlotKeyFromElement(element: {
  nodoPadre?: number
  nodo?: number
  descripcion?: string
}): string | null {
  return buildLinkedSonSlotKey(element.nodoPadre, element.nodo, element.descripcion)
}

export function buildLinkedSonSlotKeyFromChild(child: {
  parentOption?: number
  option?: number
  nodo?: number
  description?: string
}): string | null {
  const nodo = child.option ?? child.nodo
  return buildLinkedSonSlotKey(child.parentOption, nodo, child.description)
}

export function parseLinkedSonSlotKey(key: string): {
  nodoPadre: number
  nodo: number
  descripcion: string
} | null {
  const bang = key.indexOf('!')
  const slotPart = bang >= 0 ? key.slice(0, bang) : key
  const firstDash = slotPart.indexOf('-')
  if (firstDash < 0) return null
  const secondDash = slotPart.indexOf('-', firstDash + 1)
  if (secondDash < 0) return null

  const nodoPadre = parseInt(slotPart.slice(0, firstDash), 10)
  const nodo = parseInt(slotPart.slice(firstDash + 1, secondDash), 10)
  const descripcion = slotPart.slice(secondDash + 1)

  if (Number.isNaN(nodoPadre) || Number.isNaN(nodo) || !descripcion) return null
  return { nodoPadre, nodo, descripcion }
}

/** Campos de posición/rotación del API elementosEnlazados. */
export const LINKED_ELEMENT_POSITION_KEYS = [
  'posicionX',
  'posicionY',
  'posicionZ',
  'rotacionX',
  'rotacionY',
  'rotacionZ',
  'biPosicionX',
  'biPosicionY',
  'biPosicionZ',
  'bcPosicionX',
  'bcPosicionY',
  'bcPosicionZ',
  'bdPosicionX',
  'bdPosicionY',
  'bdPosicionZ',
  'aiPosicionX',
  'aiPosicionY',
  'aiPosicionZ',
  'acPosicionX',
  'acPosicionY',
  'acPosicionZ',
  'adPosicionX',
  'adPosicionY',
  'adPosicionZ',
  'biRotacionX',
  'biRotacionY',
  'biRotacionZ',
  'bcRotacionX',
  'bcRotacionY',
  'bcRotacionZ',
  'bdRotacionX',
  'bdRotacionY',
  'bdRotacionZ',
  'aiRotacionX',
  'aiRotacionY',
  'aiRotacionZ',
  'acRotacionX',
  'acRotacionY',
  'acRotacionZ',
  'adRotacionX',
  'adRotacionY',
  'adRotacionZ',
  'b2idPosicionX',
  'b2idPosicionY',
  'b2idPosicionZ',
  'b2idRotacionX',
  'b2idRotacionY',
  'b2idRotacionZ',
  'a2idPosicionX',
  'a2idPosicionY',
  'a2idPosicionZ',
  'a2idRotacionX',
  'a2idRotacionY',
  'a2idRotacionZ',
] as const

/** elementosEnlazados (unidadesGetUnidadPost) tiene prioridad; acabados2 solo rellena huecos. */
export function mergeLinkedElementPositionData(
  elementData: Record<string, unknown>,
  acabados2Data?: Record<string, unknown> | null,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...elementData }
  if (!acabados2Data) return merged

  for (const key of LINKED_ELEMENT_POSITION_KEYS) {
    const fromElement = elementData[key]
    if (fromElement !== undefined && fromElement !== null) {
      merged[key] = fromElement
      continue
    }
    const fromAcabados = acabados2Data[key]
    if (fromAcabados !== undefined && fromAcabados !== null) {
      merged[key] = fromAcabados
    }
  }

  return merged
}

export function applyLinkedElementPositionsFromApi(
  son: Record<string, unknown>,
  elementData: Record<string, unknown>,
): void {
  for (const key of LINKED_ELEMENT_POSITION_KEYS) {
    if (elementData[key] !== undefined && elementData[key] !== null) {
      son[key] = elementData[key]
    }
  }
}

export function getLinkedSonPositionPrefix(tipoNormalizado?: string): 'a' | 'b' {
  return tipoNormalizado === 'MAL' || tipoNormalizado === 'COL' ? 'a' : 'b'
}

/** Mapa de hijos enlazados solo desde elementosEnlazados de unidadesGetUnidadPost. */
export function buildAvailableLinkedElementsMap(
  elementosEnlazados: unknown,
): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>()
  if (!Array.isArray(elementosEnlazados)) return map

  for (const element of elementosEnlazados) {
    const key = buildLinkedSonSlotKeyFromElement(element as { nodoPadre?: number; nodo?: number; descripcion?: string })
    if (key) {
      map.set(key, element as Record<string, unknown>)
    }
  }
  return map
}
